import { apiHandler } from "../buyerApiHandler";

/**
 * Promo Offers admin client.
 *
 * Talks straight to the Buyer API (REACT_APP_BUYER_BASE_URL) via
 * buyerApiHandler, which attaches x-api-key — same path the coupon campaign
 * admin already uses. The Seller API is not involved.
 *
 * Offers are NOT coupons: an offer is a fixed combo from one store at a fixed
 * price with hard inventory, not a discount rule over an arbitrary cart.
 */

export type FulfilmentMode = "direct" | "prebooking";
export type PaymentMode = "online_only" | "both";
export type DeliveryChargeMode = "standard" | "per_km" | "free";

export interface OfferItemPayload {
  item_id: number;
  variant_id?: number;
  quantity: number;
  name_snapshot: string;
  price_snapshot?: number;
}

export interface OfferPayload {
  name: string;
  subtitle?: string;
  description?: string;
  banner_image_url?: string;
  store_id: number;
  offer_price: number;
  strike_price?: number;
  fulfilment_mode: FulfilmentMode;
  /** ISO 8601. Required when fulfilment_mode is "prebooking". */
  booking_opens_at?: string;
  booking_closes_at?: string;
  delivery_starts_at?: string;
  max_claims: number;
  per_user_limit?: number;
  payment_mode: PaymentMode;
  cancellation_allowed?: boolean;
  delivery_charge_mode: DeliveryChargeMode;
  delivery_charge_value?: number;
  max_delivery_km?: number;
  /** Blank/undefined = normal tax rules · 0 = no tax · 5 = 5% of subtotal. */
  tax_percent?: number;
  items: OfferItemPayload[];
}

const BASE = "/admin/promo-offers";

export const getOffers = (params?: {
  status?: string;
  store_id?: number;
  page?: number;
  limit?: number;
}) => {
  const qs = new URLSearchParams();
  if (params?.status) qs.append("status", params.status);
  if (params?.store_id) qs.append("store_id", String(params.store_id));
  qs.append("page", String(params?.page ?? 1));
  qs.append("limit", String(params?.limit ?? 20));
  return apiHandler("get", `${BASE}?${qs.toString()}`);
};

export const getOfferById = (id: string | number) =>
  apiHandler("get", `${BASE}/${id}`);

export interface StoreOption {
  id: number;
  name: string;
  status?: boolean;
  /** Branch locality — brands run several stores under one name. */
  locality?: string | null;
  city?: string | null;
  /** Menu size. A store with 0 items cannot back an offer. */
  item_count?: number;
}

export interface ItemOption {
  id: number;
  name: string;
}

/**
 * Stores and items come from the BUYER database, not the Seller API, because
 * promo_offers.store_id and promo_offer_items.item_id point at those tables.
 * Sourcing ids from the seller side could silently disagree.
 */
export const getStoreOptions = (search?: string) =>
  apiHandler(
    "get",
    `${BASE}/options/stores${search ? `?search=${encodeURIComponent(search)}` : ""}`,
  );

export const getItemOptions = (storeId: number | string, search?: string) =>
  apiHandler(
    "get",
    `${BASE}/options/stores/${storeId}/items${
      search ? `?search=${encodeURIComponent(search)}` : ""
    }`,
  );

/**
 * Pull a store's latest menu from ONDC.
 *
 * An item added in the seller panel lives in the SELLER database; offers and
 * carts reference BUYER item ids, which only exist once ONDC ingestion has
 * run. Without this, a newly added dish cannot be put in an offer until the
 * nightly 02:00 sync. The catalog arrives asynchronously, so re-fetch the
 * item list a moment after this resolves.
 */
export const refreshStoreCatalog = (storeId: number | string, city?: string) =>
  apiHandler("post", `${BASE}/options/stores/${storeId}/refresh-catalog`, {
    city,
  });

export const createOffer = (payload: OfferPayload) =>
  apiHandler("post", BASE, payload);

/**
 * Upload the offer's banner image (multipart), mirroring how bannerService
 * uploads banner art. The API stores it on S3 and returns the URL, which it
 * also writes to the offer. On publish that image becomes a real row in
 * Banner Management and appears on the app home.
 */
export const uploadOfferBanner = (id: string | number, file: File) => {
  const form = new FormData();
  form.append("image", file);
  return apiHandler("post", `${BASE}/${id}/banner`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const updateOffer = (id: string | number, payload: Partial<OfferPayload>) =>
  apiHandler("patch", `${BASE}/${id}`, payload);

/** "Sync to buyer app" — seeds the slot pool and makes the offer visible. */
export const publishOffer = (id: string | number) =>
  apiHandler("post", `${BASE}/${id}/publish`, {});

export const unpublishOffer = (id: string | number) =>
  apiHandler("post", `${BASE}/${id}/unpublish`, {});

export const cancelOffer = (id: string | number) =>
  apiHandler("post", `${BASE}/${id}/cancel`, {});

export const getOfferClaims = (id: string | number) =>
  apiHandler("get", `${BASE}/${id}/claims`);

export const getOfferAnalytics = (id: string | number) =>
  apiHandler("get", `${BASE}/${id}/analytics`);

export const reconcileOfferSlots = (id: string | number) =>
  apiHandler("post", `${BASE}/${id}/reconcile`, {});
